import React, { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { BookmarkIcon } from '@heroicons/react/24/outline'
import { Button } from 'components/ui/button'
import InputField from 'components/styledFormElements/InputField'
import FileInputUploadField from 'components/styledFormElements/FileInputUploadField'
import { useUploadFiles } from 'hooks/upload'
import { getImageSrc, getRelativeIconPath } from 'services/util'
import { useAddSettings, useSettingsData, useUpdateSettings } from 'hooks/settings'
import { useNotificationBar } from 'context/notificationContext'

interface BookmarkSettingsFormData {
    topTitle: string
    topImage: File | string | null
    title: string
    description: string
    image: File | string | null
    arrowImage: File | string | null
}

const BookmarkSettings = () => {
    const { mutate: addSettings, isLoading: addLoading } = useAddSettings()
    const { mutate: updateSettings, isLoading: updateLoading } = useUpdateSettings()
    const { data: settingsData, refetch } = useSettingsData()
    const uploadMutation = useUploadFiles()
    const notification = useNotificationBar()
    const [isSubmitting, setIsSubmitting] = useState(false)

    const {
        register,
        handleSubmit,
        getValues,
        setValue,
        formState: { errors },
        watch,
        control,
        reset,
        setError,
        clearErrors,
    } = useForm<BookmarkSettingsFormData>({
        mode: 'onChange',
        defaultValues: {
            topTitle: '',
            topImage: null,
            title: '',
            description: '',
            image: null,
            arrowImage: null,
        },
    })

    const uploadImage = async (file: File) => {
        const formData = new FormData()
        formData.append('file', file)
        formData.append('customPath', 'settings/images')

        const res = await uploadMutation.mutateAsync({
            data: formData,
        })

        return res?.data?.data?.key || ''
    }

    useEffect(() => {
        const hasExistingData =
            Array.isArray(settingsData?.data) &&
            settingsData.data.length > 0

        const existing = hasExistingData
            ? settingsData.data.find((item: any) => item.slug === 'empty_state_bookmark_setting')
            : null

        if (existing?.settings) {
            reset({
                topTitle: existing.settings.topTitle || '',
                topImage: existing.settings.topImage || null,
                title: existing.settings.title || '',
                description: existing.settings.description || '',
                image: existing.settings.image || null,
                arrowImage: existing.settings.arrowImage || null,
            })
        }
    }, [settingsData, reset])

    const onSubmit = async (data: BookmarkSettingsFormData) => {
        setIsSubmitting(true)

        const hasExistingData = Array.isArray(settingsData?.data) && settingsData.data.length > 0
        const existingSetting = hasExistingData
            ? settingsData.data.find((item: any) => item.slug === 'empty_state_bookmark_setting')
            : null

        let topImagePath: string | null = null
        let imagePath: string | null = null
        let arrowImagePath: string | null = null

        // Handle top image upload
        if (data.topImage instanceof File) {
            try {
                topImagePath = await uploadImage(data.topImage)
            } catch (error) {
                setError('topImage', {
                    type: 'upload',
                    message: 'Failed to upload top image'
                })
                setIsSubmitting(false)
                return
            }
        } else if (typeof data.topImage === 'string') {
            topImagePath = getRelativeIconPath(data.topImage)
        }

        // Handle image upload
        if (data.image instanceof File) {
            try {
                imagePath = await uploadImage(data.image)
            } catch (error) {
                setError('image', {
                    type: 'upload',
                    message: 'Failed to upload image'
                })
                setIsSubmitting(false)
                return
            }
        } else if (typeof data.image === 'string') {
            imagePath = getRelativeIconPath(data.image)
        }

        // Handle arrow image upload
        if (data.arrowImage instanceof File) {
            try {
                arrowImagePath = await uploadImage(data.arrowImage)
            } catch (error) {
                setError('arrowImage', {
                    type: 'upload',
                    message: 'Failed to upload arrow image'
                })
                setIsSubmitting(false)
                return
            }
        } else if (typeof data.arrowImage === 'string') {
            arrowImagePath = getRelativeIconPath(data.arrowImage)
        }

        const formData = {
            slug: "empty_state_bookmark_setting",
            settings: {
                topTitle: data.topTitle,
                topImage: topImagePath || null,
                title: data.title,
                description: data.description,
                image: imagePath || null,
                arrowImage: arrowImagePath || null,
            },
            status: 1,
            is_delete: 0
        }

        if (existingSetting?.id) {
            updateSettings(
                {
                    data: formData,
                    setting_id: existingSetting.id,
                },
                {
                    onSuccess: () => {
                        notification.push({
                            title: 'Success',
                            type: 'success',
                            message: 'Empty state bookmark settings updated successfully',
                        })
                        refetch()
                        setIsSubmitting(false)
                    },
                    onError: (error: any) => {
                        notification.push({
                            title: 'Error',
                            type: 'error',
                            message: 'Failed to update empty state bookmark settings',
                        })
                        setIsSubmitting(false)
                    },
                }
            )
        } else {
            addSettings(
                { data: formData },
                {
                    onSuccess: () => {
                        notification.push({
                            title: 'Success',
                            type: 'success',
                            message: 'Empty state bookmark settings created successfully',
                        })
                        refetch()
                        setIsSubmitting(false)
                    },
                    onError: (error: any) => {
                        notification.push({
                            title: 'Error',
                            type: 'error',
                            message: 'Failed to create empty state bookmark settings',
                        })
                        setIsSubmitting(false)
                    },
                }
            )
        }
    }

    return (
        <>
            {/* Empty State Bookmark Settings Section */}
            <form onSubmit={handleSubmit(onSubmit)}>
                <div className="w-full px-6 pt-6 bg-white dark:text-gray-200 dark:bg-slate-800 rounded-lg border border-gray-200 dark:border-slate-800 shadow-md">
                    <aside className="flex flex-auto items-center gap-2">
                        <BookmarkIcon className="h-6 w-6" />
                        <h4 className="text-xl">Empty State Bookmark Settings</h4>
                    </aside>

                    {/* Single Row */}
                    <div className="my-4 bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-lg p-6 shadow-sm">
                        <div className="space-y-6">
                            {/* First Row: Top Image and Top Title */}
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                {/* Top Image Field */}
                                <div>
                                    <FileInputUploadField
                                        label="Top Image"
                                        fieldName="topImage"
                                        register={register('topImage') as any}
                                        errors={{ topImage: errors?.topImage }}
                                        existingFile={typeof watch('topImage') === 'string' ? getImageSrc(watch('topImage') as string) : null}
                                        watchFile={watch('topImage') instanceof File ? watch('topImage') as File : null}
                                        onFileSelect={(file: File | null) => {
                                            if (!file) {
                                                setValue('topImage', null, { shouldValidate: true })
                                                return
                                            }
                                            setValue('topImage', file, { shouldValidate: true })
                                            clearErrors('topImage')
                                        }}
                                        tooltip={{
                                            title: 'Top image requirements',
                                            items: [
                                                'Top image on the empty state',
                                                'Upload a top image for the empty state bookmark section',
                                                'Formats: JPEG, PNG, GIF, WebP, SVG',
                                                'File size: Up to 5 MB',
                                            ],
                                            position: 'top'
                                        }}
                                        allowedTypes={['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp', 'image/svg+xml']}
                                        containerClassName="w-full h-[120px] mb-4"
                                    />
                                </div>

                                {/* Top Title Field */}
                                <div>
                                    <InputField
                                        label="Top Title"
                                        type="text"
                                        required
                                        helpText="Enter the top title for the empty state bookmark section."
                                        placeholder="Enter top title"
                                        register={register('topTitle', {
                                            required: 'Top Title is required',
                                            minLength: {
                                                value: 3,
                                                message: 'Top Title must be at least 3 characters',
                                            },
                                            maxLength: {
                                                value: 100,
                                                message: 'Top Title must not exceed 100 characters',
                                            },
                                        })}
                                        error={errors?.topTitle?.message as string}
                                    />
                                </div>
                            </div>

                            {/* Second Row: Title and Description */}
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                {/* Title Field */}
                                <div>
                                    <InputField
                                        label="Title"
                                        type="text"
                                        required
                                        helpText="Enter the title for the empty state bookmark section."
                                        placeholder="Enter title"
                                        register={register('title', {
                                            required: 'Title is required',
                                            minLength: {
                                                value: 3,
                                                message: 'Title must be at least 3 characters',
                                            },
                                            maxLength: {
                                                value: 100,
                                                message: 'Title must not exceed 100 characters',
                                            },
                                        })}
                                        error={errors?.title?.message as string}
                                    />
                                </div>

                                {/* Description Field (Textarea) */}
                                <div>
                                    <InputField
                                        label="Description"
                                        type="text"
                                        required
                                        helpText="Enter the description for the empty state bookmark section. Note: Wrap text in ## to make it bold (e.g., ##bold text##)."
                                        isTextarea
                                        row={3}
                                        placeholder="Enter description"
                                        register={register('description', {
                                            required: 'Description is required',
                                            minLength: {
                                                value: 10,
                                                message: 'Description must be at least 10 characters',
                                            },
                                            maxLength: {
                                                value: 500,
                                                message: 'Description must not exceed 500 characters',
                                            },
                                        })}
                                        error={errors?.description?.message as string}
                                    />
                                </div>
                            </div>

                            {/* Third Row: Image and Arrow Image */}
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                {/* Image Field */}
                                <div>
                                    <FileInputUploadField
                                        label="Image"
                                        fieldName="image"
                                        register={register('image') as any}
                                        errors={{ image: errors?.image }}
                                        existingFile={typeof watch('image') === 'string' ? getImageSrc(watch('image') as string) : null}
                                        watchFile={watch('image') instanceof File ? watch('image') as File : null}
                                        onFileSelect={(file: File | null) => {
                                            if (!file) {
                                                setValue('image', null, { shouldValidate: true })
                                                return
                                            }
                                            setValue('image', file, { shouldValidate: true })
                                            clearErrors('image')
                                        }}
                                        tooltip={{
                                            title: 'Image requirements',
                                            items: [
                                                'Second image inside empty state (play, arrow image)',
                                                'Upload an image for the empty state bookmark section',
                                                'Formats: JPEG, PNG, GIF, WebP, SVG',
                                                'File size: Up to 5 MB',
                                            ],
                                            position: 'top'
                                        }}
                                        allowedTypes={['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp', 'image/svg+xml']}
                                        containerClassName="w-full h-[120px] mb-4"
                                    />
                                </div>

                                {/* Arrow Image Field */}
                                <div>
                                    <FileInputUploadField
                                        label="Arrow Image"
                                        fieldName="arrowImage"
                                        register={register('arrowImage') as any}
                                        errors={{ arrowImage: errors?.arrowImage }}
                                        existingFile={typeof watch('arrowImage') === 'string' ? getImageSrc(watch('arrowImage') as string) : null}
                                        watchFile={watch('arrowImage') instanceof File ? watch('arrowImage') as File : null}
                                        onFileSelect={(file: File | null) => {
                                            if (!file) {
                                                setValue('arrowImage', null, { shouldValidate: true })
                                                return
                                            }
                                            setValue('arrowImage', file, { shouldValidate: true })
                                            clearErrors('arrowImage')
                                        }}
                                        tooltip={{
                                            title: 'Arrow image requirements',
                                            items: [
                                                'Third image goes for the bootmark',
                                                'Upload an arrow image for the empty state bookmark section',
                                                'Formats: JPEG, PNG, GIF, WebP, SVG',
                                                'File size: Up to 5 MB',
                                            ],
                                            position: 'top'
                                        }}
                                        allowedTypes={['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp', 'image/svg+xml']}
                                        containerClassName="w-full h-[120px] mb-4"
                                    />
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="flex justify-left my-6 pb-6">
                        <Button color="blue" type="submit" isLoading={isSubmitting || updateLoading || addLoading}>
                            Save Empty State Bookmark Settings
                        </Button>
                    </div>
                </div>
            </form>
        </>
    )
}

export default BookmarkSettings
